<template>
  <el-drawer
    size="70%"
    title="局部交单"
    :visible.sync="innerVisible"
    :before-close="handleClose"
    @opened="measureViewport"
  >
    <div class="partial-settle">
      <!-- 顶部操作栏 -->
      <div class="toolbar">
        <div class="toolbar-left">
          <el-checkbox
            :indeterminate="isIndeterminate"
            :value="isAllChecked"
            @change="toggleAll"
          >
            全选
          </el-checkbox>
          <span class="selected-count">
            已选 <span class="num">{{ selectedIds.length }}</span> /
            {{ totalCount }} 单
          </span>
          <el-radio-group
            v-model="groupMode"
            size="small"
            class="group-mode-radio"
            @change="switchGroupMode"
          >
            <el-radio-button label="day">按日期</el-radio-button>
            <el-radio-button label="month">按月份</el-radio-button>
          </el-radio-group>
          <span class="quick-select">
            选择第
            <el-input
              v-model="rangeStart"
              size="small"
              class="quick-input"
              placeholder="起"
              @keyup.enter.native="selectRange"
            ></el-input>
            -
            <el-input
              v-model="rangeEnd"
              size="small"
              class="quick-input"
              placeholder="止"
              @keyup.enter.native="selectRange"
            ></el-input>
            单
            <el-button size="small" @click="selectRange">确定</el-button>
          </span>
          <el-button size="small" type="text" @click="toggleExpandAll">
            {{ isAllExpanded ? "全部收起" : "全部展开" }}
          </el-button>
        </div>
        <div class="toolbar-right">
          <el-button
            size="small"
            :disabled="selectedIds.length === 0"
            @click="openSetGiveUser"
          >
            设置交卡人
          </el-button>
          <el-button
            size="small"
            type="primary"
            :loading="submitLoading"
            :disabled="selectedIds.length === 0"
            @click="generateReceipt"
          >
            生成交卡回执
          </el-button>
        </div>
      </div>

      <!-- 列标题栏(固定) -->
      <div v-if="groupList.length > 0" class="list-header cols">
        <span class="col col-check"></span>
        <span class="col col-no">运单号</span>
        <span class="col col-truck">车牌</span>
        <span class="col col-soil">土质</span>
        <span class="col col-weight">重量</span>
        <span class="col col-time">签单时间</span>
        <span class="col col-staff">签单员</span>
      </div>

      <!-- 空态 -->
      <el-empty
        v-if="!tableLoading && groupList.length === 0"
        description="无可结算运单"
      ></el-empty>

      <!-- 虚拟滚动列表:分组头与运单行混排,仅渲染可视区 -->
      <div
        v-else
        ref="viewport"
        v-loading="tableLoading"
        class="viewport"
        @scroll.passive="onScroll"
      >
        <div class="phantom" :style="{ height: totalHeight + 'px' }">
          <div
            v-for="item in visibleItems"
            :key="item.key"
            class="v-item"
            :style="{
              transform: 'translateY(' + item._top + 'px)',
              height: item._height + 'px',
            }"
          >
            <!-- 分组头 -->
            <div
              v-if="item.type === 'header'"
              class="group-header"
              @click="toggleCollapse(item.group)"
            >
              <el-checkbox
                class="group-check"
                :indeterminate="isGroupIndeterminate(item.group)"
                :value="isGroupChecked(item.group)"
                @change="toggleGroup(item.group)"
                @click.native.stop
              ></el-checkbox>
              <span class="group-date">{{ item.group.shiftDate }}</span>
              <span class="group-count">共 {{ item.group.list.length }} 单</span>
              <i
                class="arrow el-icon-arrow-down"
                :class="{ collapsed: isCollapsed(item.group) }"
              ></i>
            </div>

            <!-- 运单行 -->
            <div
              v-else
              class="waybill-row cols"
              :class="{ 'row-selected': selectedSet.has(item.row.id) }"
              @click="toggleOne(item.row)"
            >
              <span class="col col-check">
                <el-checkbox
                  :value="selectedSet.has(item.row.id)"
                  @change="toggleOne(item.row)"
                  @click.native.stop
                ></el-checkbox>
              </span>
              <span class="col col-no" :title="item.row.no">{{
                item.row.no
              }}</span>
              <span class="col col-truck" :title="item.row.truckCode">{{
                item.row.truckCode
              }}</span>
              <span class="col col-soil" :title="item.row.goSoilType">{{
                item.row.goSoilType
              }}</span>
              <span class="col col-weight">{{ item.row.weightTons }}</span>
              <span class="col col-time" :title="item.row.goDatetime">{{
                item.row.goDatetime
              }}</span>
              <span class="col col-staff" :title="item.row.goStaffName">{{
                item.row.goStaffName
              }}</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  </el-drawer>
</template>

<script>
import {
  getWaybillsByCardNos,
  generateCardReceiptByIds,
} from "@/api/chain/companysettle";

// 无班次日期运单的归组名称
const UNKNOWN_SHIFT_GROUP = "未知班次";
// 分组头行高(px)
const HEADER_HEIGHT = 44;
// 运单行高(px),需与 CSS 保持一致,否则虚拟滚动定位错位
const ROW_HEIGHT = 40;
// 可视区上下额外渲染的缓冲项数,防止快速滚动露白
const BUFFER = 6;

export default {
  name: "partialSettleDrawer",
  props: {
    // 弹窗显隐,支持 .sync
    visible: {
      type: Boolean,
      default: false,
    },
    // 结算卡号数组(多卡合并)
    cardNos: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      tableLoading: false,
      submitLoading: false,
      // 分组模式: 'day'=按日期, 'month'=按月份
      groupMode: "month",
      // 平铺运单列表(原始)
      waybillList: [],
      // 按班次日期分组 [{shiftDate, list, ids}]
      groupList: [],
      // 已折叠的分组 {shiftDate: true}
      collapsed: {},
      // 已选中的运单id(保持数组以维持 Vue2 响应式)
      selectedIds: [],
      // 缓存选中运单涉及的卡号,避免每次计算遍历1万条(性能优化)
      cachedInvolvedCardNos: [],
      // 快速选择范围(第起-止单)的输入值
      rangeStart: "",
      rangeEnd: "",
      // 滚动位置与视口高度(驱动虚拟滚动)
      scrollTop: 0,
      viewportHeight: 500,
      // requestAnimationFrame 句柄,用于 scroll 节流
      rafId: null,
    };
  },
  computed: {
    // 弹窗显隐内部代理,避免直接改 prop
    innerVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      },
    },
    // 选中id集合,O(1) 查找,随 selectedIds 变化重建一次
    selectedSet() {
      return new Set(this.selectedIds);
    },
    // 选中运单涉及的卡号(去重),用于设置交卡人
    // 使用缓存,避免频繁遍历1万条数据(由 watcher 维护)
    involvedCardNos() {
      return this.cachedInvolvedCardNos;
    },
    // 全部运单总数
    totalCount() {
      return this.waybillList.length;
    },
    // 是否已全选
    isAllChecked() {
      return this.totalCount > 0 && this.selectedIds.length === this.totalCount;
    },
    // 全选框半选状态
    isIndeterminate() {
      return (
        this.selectedIds.length > 0 && this.selectedIds.length < this.totalCount
      );
    },
    // 是否全部展开
    isAllExpanded() {
      return (
        this.groupList.length > 0 && Object.keys(this.collapsed).length === 0
      );
    },
    // 每个分组已选中的数量(一次性算,供分组头 O(1) 读取)
    groupSelectedCount() {
      let stat = {};
      let set = this.selectedSet;
      for (let i = 0; i < this.groupList.length; i++) {
        let group = this.groupList[i];
        let count = 0;
        for (let j = 0; j < group.ids.length; j++) {
          if (set.has(group.ids[j])) {
            count++;
          }
        }
        stat[group.shiftDate] = count;
      }
      return stat;
    },
    // 扁平项列表:分组头 + (未折叠时)其运单行,虚拟滚动的数据源
    flatItems() {
      let items = [];
      let collapsedMap = this.collapsed;
      for (let i = 0; i < this.groupList.length; i++) {
        let group = this.groupList[i];
        // 冻结静态对象,减少响应式追踪开销
        items.push(
          Object.freeze({
            type: "header",
            key: "h_" + group.shiftDate,
            group: group,
          })
        );
        if (!collapsedMap[group.shiftDate]) {
          let list = group.list;
          for (let j = 0; j < list.length; j++) {
            items.push(
              Object.freeze({
                type: "row",
                key: "r_" + list[j].id,
                row: list[j],
                group: group,
              })
            );
          }
        }
      }
      return items;
    },
    // 每项的 top 偏移与高度,以及列表总高度;仅在 flatItems 变化时重算
    itemPositions() {
      let positions = [];
      let top = 0;
      for (let i = 0; i < this.flatItems.length; i++) {
        let h = this.flatItems[i].type === "header" ? HEADER_HEIGHT : ROW_HEIGHT;
        positions.push({ top: top, height: h });
        top += h;
      }
      return { positions: positions, totalHeight: top };
    },
    totalHeight() {
      return this.itemPositions.totalHeight;
    },
    // 当前可视区应渲染的项(含缓冲),数量恒定与总量无关
    visibleItems() {
      let positions = this.itemPositions.positions;
      if (positions.length === 0) {
        return [];
      }
      let start = this.findStartIndex(this.scrollTop);
      let bottom = this.scrollTop + this.viewportHeight;
      let end = start;
      while (end < positions.length && positions[end].top < bottom) {
        end++;
      }
      start = Math.max(0, start - BUFFER);
      end = Math.min(positions.length, end + BUFFER);

      let result = [];
      for (let i = start; i < end; i++) {
        let item = this.flatItems[i];
        result.push(
          Object.assign({}, item, {
            _top: positions[i].top,
            _height: positions[i].height,
          })
        );
      }
      return result;
    },
  },
  watch: {
    // 父组件保留实例(非 v-if)时,靠 watcher 在打开时加载
    visible(val) {
      if (val) {
        this.resetState();
        this.getWaybillList();
      }
    },
    // 监听选中状态变化,更新涉及的卡号缓存(优化设置交卡人性能)
    selectedIds: {
      handler() {
        this.updateInvolvedCardNos();
      },
      immediate: false,
    },
  },
  mounted() {
    // 父组件用 v-if 挂载时,创建即已 visible=true,watcher 不触发,需在此加载
    if (this.visible) {
      this.resetState();
      this.getWaybillList();
    }
  },
  beforeDestroy() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
  },
  methods: {
    // 重置内部状态,避免复用残留
    resetState() {
      this.waybillList = [];
      this.groupList = [];
      this.collapsed = {};
      this.selectedIds = [];
      this.cachedInvolvedCardNos = [];
      this.scrollTop = 0;
    },
    // 关闭弹窗
    handleClose() {
      this.innerVisible = false;
    },
    // 二分查找第一个底边越过 scrollTop 的项索引
    findStartIndex(scrollTop) {
      let positions = this.itemPositions.positions;
      let lo = 0;
      let hi = positions.length - 1;
      let ans = positions.length;
      while (lo <= hi) {
        let mid = (lo + hi) >> 1;
        if (positions[mid].top + positions[mid].height > scrollTop) {
          ans = mid;
          hi = mid - 1;
        } else {
          lo = mid + 1;
        }
      }
      return ans === positions.length ? Math.max(0, positions.length - 1) : ans;
    },
    // 滚动处理,用 rAF 节流,避免频繁触发计算
    onScroll(e) {
      let target = e.target;
      if (this.rafId) {
        return;
      }
      let self = this;
      this.rafId = requestAnimationFrame(function () {
        self.scrollTop = target.scrollTop;
        self.rafId = null;
      });
    },
    // 测量视口可视高度
    measureViewport() {
      let self = this;
      this.$nextTick(function () {
        if (self.$refs.viewport) {
          self.viewportHeight = self.$refs.viewport.clientHeight || 500;
        }
      });
    },
    // 加载多卡合并的可结算运单(A卡查本卡,B卡查全部子卡,后端已处理)
    getWaybillList() {
      if (!this.cardNos || this.cardNos.length === 0) {
        return;
      }
      this.tableLoading = true;
      let self = this;
      getWaybillsByCardNos(this.cardNos)
        .then(function (response) {
          let list = (response.data && response.data.data) || [];
          // 冻结每个运单对象,避免 Vue 对上万条数据做深度响应式劫持(卡顿主因)
          // 运单数据只读,选中状态由 selectedIds 单独维护,冻结不影响交互
          for (let i = 0; i < list.length; i++) {
            Object.freeze(list[i]);
          }
          self.waybillList = Object.freeze(list);
          self.buildGroups();
          self.tableLoading = false;
          self.measureViewport();
        })
        .catch(function () {
          self.tableLoading = false;
        });
    },
    // 按班次日期(goShiftTime)分组,后端返回平铺列表故前端归组
    // groupMode='day': 按日期(2026-07-08)分组
    // groupMode='month': 按月份(2026-07)分组
    buildGroups() {
      let groupMap = {};
      let orderList = [];
      let isMonthMode = this.groupMode === "month";
      // 第一遍遍历:分组聚合
      for (let i = 0; i < this.waybillList.length; i++) {
        let row = this.waybillList[i];
        let shiftDate = row.goShiftTime ? row.goShiftTime : UNKNOWN_SHIFT_GROUP;
        // 按月份模式时,截取年月(yyyy-mm)
        if (isMonthMode && shiftDate !== UNKNOWN_SHIFT_GROUP) {
          shiftDate = shiftDate.substring(0, 7); // "2026-07-08" -> "2026-07"
        }
        if (!groupMap[shiftDate]) {
          groupMap[shiftDate] = { shiftDate: shiftDate, list: [], ids: [] };
          orderList.push(shiftDate);
        }
        groupMap[shiftDate].list.push(row);
        groupMap[shiftDate].ids.push(row.id);
      }
      // 按班次日期倒序,未知班次置底
      orderList.sort(function (a, b) {
        if (a === UNKNOWN_SHIFT_GROUP) {
          return 1;
        }
        if (b === UNKNOWN_SHIFT_GROUP) {
          return -1;
        }
        return a < b ? 1 : -1;
      });
      // 构建最终数组(一次性赋值,减少响应式开销)
      let result = [];
      for (let k = 0; k < orderList.length; k++) {
        result.push(groupMap[orderList[k]]);
      }
      this.groupList = result;
      // 清空折叠状态(默认全部展开)
      this.collapsed = {};
    },
    // 分组折叠状态
    isCollapsed(group) {
      return !!this.collapsed[group.shiftDate];
    },
    toggleCollapse(group) {
      let key = group.shiftDate;
      if (this.collapsed[key]) {
        this.$delete(this.collapsed, key);
      } else {
        this.$set(this.collapsed, key, true);
      }
    },
    // 分组是否全选
    isGroupChecked(group) {
      return (
        group.ids.length > 0 &&
        this.groupSelectedCount[group.shiftDate] === group.ids.length
      );
    },
    // 分组是否半选
    isGroupIndeterminate(group) {
      let count = this.groupSelectedCount[group.shiftDate] || 0;
      return count > 0 && count < group.ids.length;
    },
    // 切换分组全选/全不选
    toggleGroup(group) {
      if (this.isGroupChecked(group)) {
        let groupIdSet = new Set(group.ids);
        this.selectedIds = this.selectedIds.filter(function (id) {
          return !groupIdSet.has(id);
        });
      } else {
        let merged = this.selectedIds.slice();
        let curSet = this.selectedSet;
        for (let i = 0; i < group.ids.length; i++) {
          if (!curSet.has(group.ids[i])) {
            merged.push(group.ids[i]);
          }
        }
        this.selectedIds = merged;
      }
    },
    // 切换单条勾选
    toggleOne(row) {
      if (this.selectedSet.has(row.id)) {
        this.selectedIds = this.selectedIds.filter(function (id) {
          return id !== row.id;
        });
      } else {
        this.selectedIds = this.selectedIds.concat(row.id);
      }
    },
    // 顶部全选/全不选
    toggleAll() {
      if (this.isAllChecked) {
        this.selectedIds = [];
      } else {
        let all = [];
        for (let i = 0; i < this.waybillList.length; i++) {
          all.push(this.waybillList[i].id);
        }
        // 大数据量时显示 loading,用 setTimeout 推到宏任务让 UI 先响应
        let self = this;
        let loading = this.$loading({
          lock: true,
          text: '正在选中 ' + all.length + ' 单...',
          background: 'rgba(255, 255, 255, 0.7)',
        });
        setTimeout(function() {
          self.selectedIds = all;
          self.$nextTick(function() {
            loading.close();
          });
        }, 50);
      }
    },
    // 快速选中"第 start - end 单"(按分组展示顺序,即用户从上往下的序号,含两端)
    selectRange() {
      let start = parseInt(this.rangeStart, 10);
      let end = parseInt(this.rangeEnd, 10);
      if (!start || !end || start <= 0 || end <= 0) {
        this.$message.warning("请输入有效的起止序号");
        return;
      }
      if (start > end) {
        this.$message.warning("起始序号不能大于结束序号");
        return;
      }
      if (start > this.totalCount) {
        this.$message.warning("起始序号超出总单数(" + this.totalCount + ")");
        return;
      }
      if (end > this.totalCount) {
        end = this.totalCount;
      }
      // 按 groupList 展开顺序构建平铺id序列(与列表可视顺序一致),取 [start, end] 区间(1基,含两端)
      let picked = [];
      let idx = 0; // 已遍历到的序号(1基)
      for (let i = 0; i < this.groupList.length && idx < end; i++) {
        let ids = this.groupList[i].ids;
        for (let j = 0; j < ids.length && idx < end; j++) {
          idx++;
          if (idx >= start) {
            picked.push(ids[j]);
          }
        }
      }
      this.selectedIds = picked;
      this.$message.success(
        "已选中第 " + start + " - " + end + " 单,共 " + picked.length + " 单"
      );
    },
    // 展开/收起全部分组
    toggleExpandAll() {
      if (this.isAllExpanded) {
        let c = {};
        for (let i = 0; i < this.groupList.length; i++) {
          c[this.groupList[i].shiftDate] = true;
        }
        this.collapsed = c;
      } else {
        this.collapsed = {};
      }
    },
    // 切换分组模式(日期/月份)
    switchGroupMode() {
      // 显示 loading 避免卡顿感知
      let loading = this.$loading({
        lock: true,
        text: '正在切换分组...',
        background: 'rgba(255, 255, 255, 0.7)',
      });
      let self = this;
      // 用 setTimeout 推到宏任务,让 UI 先响应
      setTimeout(function() {
        // 保持选中状态,仅重新分组
        self.buildGroups();
        // 滚动回顶部
        self.scrollTop = 0;
        self.$nextTick(function() {
          if (self.$refs.viewport) {
            self.$refs.viewport.scrollTop = 0;
          }
          loading.close();
        });
      }, 50);
    },
    // 更新选中运单涉及的卡号缓存(增量计算,避免遍历全部1万条)
    updateInvolvedCardNos() {
      let set = this.selectedSet;
      let seen = {};
      let result = [];
      // 优化:仅遍历分组数据(已按日期/月份聚合),比遍历原始1万条快
      for (let i = 0; i < this.groupList.length; i++) {
        let group = this.groupList[i];
        for (let j = 0; j < group.list.length; j++) {
          let row = group.list[j];
          if (set.has(row.id) && !seen[row.cardNo]) {
            seen[row.cardNo] = true;
            result.push(row.cardNo);
          }
        }
      }
      this.cachedInvolvedCardNos = result;
    },
    // 按卡分组选中运单id: { cardNo: [id, ...] }
    groupSelectedByCard() {
      let map = {};
      let set = this.selectedSet;
      for (let i = 0; i < this.waybillList.length; i++) {
        let row = this.waybillList[i];
        if (set.has(row.id)) {
          if (!map[row.cardNo]) {
            map[row.cardNo] = [];
          }
          map[row.cardNo].push(row.id);
        }
      }
      return map;
    },
    // 设置交卡人:交由父组件用统一表单处理(含开启回执时的收款信息),卡号取选中运单涉及的卡
    openSetGiveUser() {
      if (this.selectedIds.length === 0) {
        this.$message.warning("请选择运单");
        return;
      }
      this.$emit("set-give-user", this.involvedCardNos.slice());
    },
    // 按选中运单id生成交卡回执(需先设置交卡人)
    generateReceipt() {
      if (this.selectedIds.length === 0) {
        this.$message.warning("请选择运单");
        return;
      }
      let self = this;
      this.$confirm(
        "确认对已选 " + this.selectedIds.length + " 单生成交卡回执?",
        "提示",
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        }
      )
        .then(function () {
          let grouped = self.groupSelectedByCard();
          // 不传交单时间与交卡人:后端从DB未回执记录取交卡人,交单时间用当前时间
          let data = Object.keys(grouped).map(function (cardNo) {
            return {
              settleCardNo: cardNo,
              ids: grouped[cardNo],
            };
          });
          self.submitLoading = true;
          generateCardReceiptByIds(data)
            .then(function (res) {
              self.submitLoading = false;
              self.$message.success(
                (res.data && res.data.msg) || "生成成功"
              );
              self.innerVisible = false;
              self.$emit("refresh");
            })
            .catch(function () {
              self.submitLoading = false;
            });
        })
        .catch(function () {});
    },
  },
};
</script>

<style lang="scss" scoped>
.partial-settle {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0 24px 24px;
  box-sizing: border-box;
}
.toolbar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #ebeef5;
  .toolbar-left {
    display: flex;
    align-items: center;
    .selected-count {
      margin: 0 16px;
      font-size: 14px;
      color: #606266;
      .num {
        color: #f56c6c;
        font-weight: bold;
      }
    }
    .group-mode-radio {
      margin-right: 16px;
    }
    .quick-select {
      display: flex;
      align-items: center;
      margin-right: 16px;
      font-size: 14px;
      color: #606266;
      .quick-input {
        width: 90px;
        margin: 0 6px;
      }
      .el-button {
        margin-left: 8px;
      }
    }
  }
}
// 列布局:标题栏与运单行共用,保证列对齐
.cols {
  display: flex;
  align-items: center;
  .col {
    padding: 0 8px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 13px;
  }
  .col-check {
    flex: 0 0 44px;
  }
  .col-no {
    flex: 2 1 0;
  }
  .col-truck {
    flex: 1.2 1 0;
  }
  .col-soil {
    flex: 1 1 0;
  }
  .col-weight {
    flex: 1 1 0;
  }
  .col-time {
    flex: 1.6 1 0;
  }
  .col-staff {
    flex: 1.2 1 0;
  }
}
.list-header {
  flex: 0 0 auto;
  height: 40px;
  background: #f5f7fa;
  color: #909399;
  font-weight: 500;
  border-bottom: 1px solid #ebeef5;
}
.viewport {
  flex: 1 1 auto;
  overflow-y: auto;
  position: relative;
}
.phantom {
  position: relative;
  width: 100%;
}
.v-item {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  will-change: transform;
}
.group-header {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 8px;
  background: #fafafa;
  border-bottom: 1px solid #ebeef5;
  cursor: pointer;
  .group-check {
    margin-right: 8px;
  }
  .group-date {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
  }
  .group-count {
    margin-left: 12px;
    font-size: 13px;
    color: #909399;
  }
  .arrow {
    margin-left: auto;
    transition: transform 0.2s;
    color: #909399;
    &.collapsed {
      transform: rotate(-90deg);
    }
  }
}
.waybill-row {
  height: 100%;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  &:hover {
    background: #f5f7fa;
  }
  &.row-selected {
    background: #ecf5ff;
  }
}
</style>
